// "What to do next" for a reschedule (date change) ticket, in plain words, the
// same way NextActionCard does it for cancellations: one clear instruction, the
// whole job as numbered steps, and, when the automatic change stopped, why.

import { isReschedulePaid } from "./ReschedulingPanel";

// support_tickets.reissued_tbo_booking_id while a change is running or unclear
// (server: modules/reschedule/reschedule.service.js).
const CHANGING = ["REISSUING", "VERIFYING"];
const UNCLEAR = "UNVERIFIED";

const hasFee = (t) =>
  t?.reschedule_fee != null && String(t.reschedule_fee).trim() !== "" && Number.isFinite(Number(t.reschedule_fee));
const marker = (t) => String(t?.reissued_tbo_booking_id || "");
const isClosed = (t) => t?.status === "Closed" || t?.status === "closed_no_response";
const feeReversed = (t) => String(t?.reschedule_payment_status || "").toLowerCase() === "reversed";
// The chat's automatic reschedule (server: openAutomationTicket): "running" while
// it works, "ended_unpaid" when closed because the customer did not pay.
const automation = (t) => t?.reschedule_details_parsed?.automation?.status || null;

/**
 * Why the automatic change stopped, in plain words, from the newest
 * "AUTOMATED REISSUE ESCALATED" entry in the ticket history. null when the
 * system never handed this ticket over.
 */
export function stoppedReason(ticket) {
  const history = Array.isArray(ticket?.history) ? ticket.history : [];
  const entries = history
    .filter((h) => /AUTOMATED REISSUE ESCALATED/i.test(h?.action_taken || h?.action || ""))
    .sort((a, b) => new Date(b.created_at || b.createdAt || 0) - new Date(a.created_at || a.createdAt || 0));
  const text = entries[0]?.action_taken || entries[0]?.action || "";
  if (!text) return null;
  const step = (text.match(/Failed step:\s*(.+)/) || [])[1]?.trim() || "";
  const detail = (text.match(/Error:\s*([\s\S]*?)(?:\n\n|$)/) || [])[1]?.trim() || "";
  return { step, detail, plain: plainReason(step, detail) };
}

function plainReason(step, detail) {
  const s = step.toLowerCase();
  const money = (detail.match(/now ₹([\d,.]+), collected ₹([\d,.]+) \(₹([\d,.]+) more\)/) || []).slice(1);
  if (s.includes("outcome unverified")) {
    return "We sent the change to the airline but did not get a clear answer back. The flight may or may not have changed.";
  }
  if (s === "fare validation" && money.length) {
    return `The price went up after the customer paid. It is now ₹${money[0]}, the customer paid ₹${money[1]}, so ₹${money[2]} more is needed.`;
  }
  if (s === "fare validation") return "The airline did not give a clear price for the change, so the system could not check it against what the customer paid.";
  if (s === "flight validation" || s === "reissue search") {
    return "The flight the customer picked is no longer available, or its timing changed.";
  }
  if (s.includes("farequote")) return "The airline did not return a price for the new flight.";
  if (s === "ticketreissue response") return "The airline refused the change.";
  if (s === "passenger lookup") {
    return /cancelled/i.test(detail)
      ? "Some passengers on this booking are already cancelled. The airline only allows changing the whole booking online."
      : "The system could not find the passenger details for this booking.";
  }
  if (s === "gst details") return "This fare needs the company GST details, but the booking does not have them.";
  if (s === "payment initiation") return "The system could not create the payment link for the customer.";
  if (s === "post-reissue state update") {
    return "The airline changed the flight, but our system could not save the new details. Do not change the flight again.";
  }
  if (s.includes("credit line fee")) return "The fee changed after the customer agreed to pay it from their credit line.";
  if (s === "tbo auth") return "The system could not connect to TBO at that moment.";
  if (s.includes("booking") || s.includes("selection")) return "Some booking or flight details were missing, so the system could not continue.";
  return "The automatic change could not finish.";
}

/** The ticket's state in a few words, with a colour. */
export function plainRescheduleStatus(t) {
  if (automation(t) === "running") return { text: "Automatic: no action needed", tone: "blue" };
  if (isClosed(t) && automation(t) === "ended_unpaid") return { text: "Closed: customer did not pay", tone: "gray" };
  if (isClosed(t)) return { text: "Flight changed", tone: "green" };
  if (marker(t) === UNCLEAR) return { text: "Check if the flight changed", tone: "red" };
  if (CHANGING.includes(marker(t))) return { text: "Changing the flight now", tone: "blue" };
  if (feeReversed(t)) return { text: "Not changed: money returned", tone: "amber" };
  if (isReschedulePaid(t)) {
    return t?.status === "Escalated" ? { text: "Paid: change the flight", tone: "red" } : { text: "Paid: changing the flight", tone: "blue" };
  }
  if (t?.reschedule_payment_link_id) return { text: "Waiting for the customer to pay", tone: "amber" };
  if (!t?.reschedule_details_parsed) return { text: "Waiting for the customer's new flight", tone: "gray" };
  return { text: "Needs you", tone: "red" };
}

const TONES = {
  green: "bg-green-100 text-green-800 border-green-200",
  red: "bg-red-100 text-red-800 border-red-200",
  amber: "bg-amber-100 text-amber-900 border-amber-200",
  blue: "bg-blue-100 text-blue-800 border-blue-200",
  gray: "bg-gray-100 text-gray-700 border-gray-200",
};

export function RescheduleStatusChip({ ticket }) {
  const st = plainRescheduleStatus(ticket);
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${TONES[st.tone]}`}>{st.text}</span>;
}

/** The one thing to do now. */
export function rescheduleNextAction(t) {
  if (automation(t) === "running") {
    return {
      title: "Being done automatically",
      text: "The customer is rescheduling in the chat and pays there. No action needed: this ticket closes on its own, or comes back to you if anything fails.",
      tone: "blue",
    };
  }
  if (isClosed(t) && automation(t) === "ended_unpaid") {
    return { title: "Closed: the customer did not pay", text: "The flight was not changed. Nothing more to do.", tone: "gray" };
  }
  if (isClosed(t)) {
    return { title: "Done: the flight has been changed", text: "The booking shows the new flight and the customer has been told. Nothing more to do.", tone: "green" };
  }
  if (marker(t) === UNCLEAR) {
    return {
      title: "Check if the flight was changed",
      text:
        "We asked the airline to change the flight but did not get a clear answer. Do NOT change it again yourself, " +
        'or the customer could be charged twice. Click "Check with the airline" below.',
      tone: "red",
    };
  }
  if (CHANGING.includes(marker(t))) {
    return { title: "The flight is being changed now", text: "Please wait. This page updates on its own when it is done.", tone: "blue" };
  }
  if (feeReversed(t)) {
    return {
      title: "The flight was not changed",
      text:
        "The airline could not make the change, so the fee was returned to the customer's credit line. Call the customer, " +
        "explain, and help them pick another flight if they still want to change.",
      tone: "amber",
    };
  }
  if (isReschedulePaid(t)) {
    return t?.status === "Escalated"
      ? {
          title: "The customer has paid. Change the flight.",
          text: 'The automatic change did not finish (see "Why it stopped" below). Use the last step below to change the flight.',
          tone: "red",
        }
      : { title: "Payment received", text: "The system is changing the flight now. This page updates on its own.", tone: "blue" };
  }
  if (t?.reschedule_payment_link_id) {
    return {
      title: "Waiting for the customer to pay",
      text: "The customer has the payment link in the app. This page checks for the payment every few seconds.",
      tone: "amber",
    };
  }
  if (Number(t?.tbo_verified) === 1) {
    return { title: "Send the payment link", text: "The customer knows the fee. Send them the payment link in step 4 below.", tone: "red" };
  }
  if (hasFee(t)) {
    return { title: "Tell the customer the fee", text: "The fee is saved. In step 3 below, send it to the customer so they know what they will pay.", tone: "red" };
  }
  if (t?.reschedule_details_parsed) {
    return {
      title: "Get the airline's change fee",
      text: 'Check the new flight the customer wants, then click "Get the fee from the airline" in step 2 below and save the fee.',
      tone: "red",
    };
  }
  return {
    title: "Waiting for the customer to pick a new flight",
    text: "The customer has not chosen the new flight in the app yet. It will show here as soon as they do.",
    tone: "gray",
  };
}

/** The whole job as numbered steps: done, current (the first not done) or to come. */
export function rescheduleJobSteps(t) {
  const paid = isReschedulePaid(t);
  const done = isClosed(t);
  const steps = [
    { text: "Check the new flight the customer wants", done: !!t?.reschedule_details_parsed || paid || done },
    { text: "Get the change fee from the airline", done: hasFee(t) || paid || done },
    { text: "Tell the customer the fee", done: Number(t?.tbo_verified) === 1 || !!t?.reschedule_payment_link_id || paid || done },
    { text: "Send the payment link", done: !!t?.reschedule_payment_link_id || paid || done },
    { text: "Customer pays", done: paid || done },
    {
      text: t?.status === "Escalated" && paid ? "Change the flight (do it below: the automatic change stopped)" : "Change the flight",
      done,
    },
  ];
  const current = steps.findIndex((x) => !x.done);
  return steps.map((x, i) => ({ ...x, state: x.done ? "done" : i === current ? "current" : "todo" }));
}

function StepList({ steps }) {
  return (
    <ol className="mt-3 space-y-1.5" data-testid="reschedule-steps">
      {steps.map((x, i) => (
        <li key={x.text} className="flex items-center gap-2 text-sm">
          <span
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
              x.state === "done" ? "bg-green-600 text-white" : x.state === "current" ? "bg-blue-600 text-white ring-4 ring-blue-200" : "bg-gray-200 text-gray-600"
            }`}
          >
            {x.state === "done" ? "✓" : i + 1}
          </span>
          <span className={x.state === "done" ? "text-gray-500 line-through" : x.state === "current" ? "font-semibold text-gray-900" : "text-gray-500"}>
            Step {i + 1}: {x.text}
          </span>
          {x.state === "current" ? <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-800">Now</span> : null}
        </li>
      ))}
    </ol>
  );
}

export default function RescheduleNextActionCard({ ticket }) {
  if (!ticket) return null;
  const next = rescheduleNextAction(ticket);
  const reason = !isClosed(ticket) ? stoppedReason(ticket) : null;
  const box = {
    red: "border-red-300 bg-red-50",
    amber: "border-amber-300 bg-amber-50",
    green: "border-green-300 bg-green-50",
    blue: "border-blue-300 bg-blue-50",
    gray: "border-gray-300 bg-gray-50",
  }[next.tone || "red"];

  return (
    <div className={`rounded-2xl border-2 px-5 py-4 ${box}`} data-testid="reschedule-next-action">
      <div className="text-xs font-bold uppercase tracking-wide text-gray-500">What to do next</div>
      <div className="mt-1 text-lg font-bold text-gray-900">{next.title}</div>
      <p className="mt-1 text-sm text-gray-700">{next.text}</p>
      {reason ? (
        <div className="mt-3 rounded-xl border border-red-200 bg-white px-3 py-2" data-testid="reschedule-stopped-reason">
          <div className="text-xs font-bold uppercase tracking-wide text-red-700">Why it stopped</div>
          <p className="mt-0.5 text-sm text-gray-800">{reason.plain}</p>
        </div>
      ) : null}
      {!feeReversed(ticket) && <StepList steps={rescheduleJobSteps(ticket)} />}
    </div>
  );
}
